import { areComparable, areComparableLight, multiplicate, roundToPrecision, toComparable } from './tools';
// region Compute Functions
class ComputeFunctions {
    product;
    productInput;
    culture;
    property;
    mappings;
    shouldUseNumericValues;
    constructor(product, productInput, culture = {}, property, mappings, shouldUseNumericValues) {
        this.product = product;
        this.productInput = productInput;
        this.culture = culture;
        this.property = property;
        this.mappings = mappings;
        this.shouldUseNumericValues = shouldUseNumericValues;
    }
    stored = {};
    getInnerData = (obj, path) => {
        if (typeof obj !== 'object' || obj === null) {
            return;
        }
        let value = obj;
        for (let i = 0; path && i < path.length && value; i++) {
            let segment = path[i];
            const isOptional = segment[segment.length - 1] === '?';
            if (isOptional) {
                segment = segment.slice(0, -1);
            }
            if (value === undefined || value === null || typeof value !== 'object') {
                if (isOptional) {
                    // return previous value
                    return value;
                }
                return undefined;
            }
            value = Reflect.get(value, segment);
        }
        return value;
    };
    getCurrent = (path) => {
        const inputValue = this.getInnerData(this.productInput, path);
        if (inputValue !== undefined) {
            return inputValue;
        }
        return this.getInnerData(this.product, path);
    };
    /** set instance culture language to the carried value  */
    setLanguage = (language) => (this.culture.language = language);
    /** store last computed value in a variable */
    storeValue = (carriedValue, key) => (this.stored[key] = carriedValue);
    getStored = (_, key) => this.stored[key];
    /** Get last computed value */
    getCarriedValue = (carriedValue) => carriedValue;
    get = (carriedValue, path, useRootValue) => {
        if (carriedValue && !useRootValue) {
            return this.getInnerData(carriedValue, path);
        }
        return this.getCurrent(path);
    };
    /** Function that tries to find an attribute first in the product input, then in the product */
    getProductAttributeById = (_, id) => {
        const inputAttribute = this.productInput?.attributes?.find((attribute) => attribute.id === id);
        if (inputAttribute) {
            return inputAttribute.delete || inputAttribute.options === null ? null : inputAttribute;
        }
        if (!this.product?.attributes) {
            return;
        }
        if (Array.isArray(this.product.attributes)) {
            // SimplifiedProduct
            return this.product?.attributes?.find((a) => a.id === id);
        }
        // BaseProduct
        return this.product?.attributes?.[id];
    };
    getProductAttributeValue = (carriedValue) => {
        if (typeof carriedValue !== 'object' || carriedValue === null || carriedValue === undefined) {
            return;
        }
        const attribute = carriedValue;
        if ('options' in attribute) {
            // SimplifiedProduct / ProductInput
            return attribute.options?.map((option) => (typeof option === 'object' ? option.defaultName : option));
        }
        // BaseProduct
        if ('terms' in attribute && attribute.terms !== undefined) {
            return attribute.terms;
        }
        if ('staticValue' in attribute && attribute.staticValue !== undefined) {
            return [attribute.staticValue];
        }
    };
    getProductAttributeOptionIds = (carriedValue) => {
        if (typeof carriedValue !== 'object' || carriedValue === null || carriedValue === undefined) {
            return;
        }
        const attribute = carriedValue;
        if ('options' in attribute) {
            // SimplifiedProduct / ProductInput
            // fails for ProductInput since they don't contain option id
            return attribute.options?.reduce((acc, option) => {
                const value = typeof option === 'object' ? ('id' in option ? option.id : undefined) : option;
                if (value !== undefined) {
                    acc.push(value);
                }
                return acc;
            }, []);
        }
        // BaseProduct
        // note: does not check attribute setting hasTerms. dependant on no old data existing (both staticValue and terms) after changing attribute type. will otherwise use terms
        if ('terms' in attribute && attribute.terms !== undefined) {
            return attribute.terms;
        }
        if ('staticValue' in attribute && attribute.staticValue !== undefined) {
            return [attribute.staticValue];
        }
    };
    getProductAttributeOptionReferences = (carriedValue) => {
        if (typeof carriedValue !== 'object' || carriedValue === null || carriedValue === undefined) {
            return;
        }
        const attribute = carriedValue;
        if ('options' in attribute) {
            // SimplifiedProduct / ProductInput
            // fails for ProductInput since they don't contain option id
            return attribute.options?.reduce((acc, option) => {
                if (option === undefined) {
                    return acc;
                }
                let value;
                if (typeof option === 'object') {
                    value = 'id' in option ? { id: option.id } : { defaultName: option.defaultName };
                }
                else {
                    value = option !== undefined ? { defaultName: option } : undefined;
                }
                if (value !== undefined) {
                    acc.push(value);
                }
                return acc;
            }, []);
        }
        // BaseProduct
        // note: does not check attribute setting hasTerms. dependant on no old data existing (both staticValue and terms) after changing attribute type. will otherwise use terms
        if ('terms' in attribute && attribute.terms !== undefined) {
            return attribute.terms.map((termId) => ({ id: termId }));
        }
        if ('staticValue' in attribute && attribute.staticValue !== undefined) {
            return [{ defaultName: attribute.staticValue }];
        }
    };
    /** Uses instance culture to find cultural data of the ProductInput or SimplifiedProduct */
    getCulturalData = (_, partialPath, type) => {
        const culturalVar = type === 'countrySpecific' ? this.culture.country : this.culture.language;
        if (!culturalVar) {
            return;
        }
        const fullPath = [type, culturalVar, ...partialPath];
        return this.getCurrent(fullPath);
    };
    getVolume = (_) => {
        if (!this.property?.customPath) {
            return;
        }
        const editedValue = this.getInnerData(this.productInput, this.property.customPath);
        const originalValue = this.getInnerData(this.product, this.property.customPath);
        const dimensions = [
            editedValue?.width === undefined ? originalValue?.width : editedValue.width,
            editedValue?.height === undefined ? originalValue?.height : editedValue.height,
            editedValue?.length == undefined ? originalValue?.length : editedValue.length,
        ].filter((d) => !!d || d === 0);
        if (dimensions.length === 3) {
            const result = roundToPrecision(multiplicate(dimensions), 0);
            if (!isNaN(result)) {
                return this.shouldUseNumericValues ? result : String(result).replaceAll('.', ',');
            }
        }
        return undefined;
    };
    getMappings = (_) => this.mappings;
    toComparable = toComparable;
    find = (haystack, itemPath, expected, comparison) => {
        if (!Array.isArray(haystack)) {
            return;
        }
        return haystack?.find((item) => {
            const data = this.getInnerData(item, itemPath);
            switch (comparison) {
                case 'comparable':
                    return areComparable(data, expected);
                // case 'equals':
                //   return data === expected
                default:
                    return data === expected;
                // return false
            }
        });
    };
    findByReference = (haystack, itemPath, expected) => {
        if (!Array.isArray(haystack) || expected === undefined) {
            return;
        }
        return haystack?.find((item) => {
            const data = this.getInnerData(item, itemPath);
            if (typeof data !== 'object' || !data) {
                return false;
            }
            if (expected.id !== undefined) {
                return 'id' in data && data.id === expected.id;
            }
            return 'defaultName' in data && areComparableLight(data.defaultName, expected.defaultName);
        });
    };
    /** unlike join does not use carried value, but is a wrapper function around an array that concatenates the content */
    concatenate = (_, args) => {
        if (!Array.isArray(args)) {
            return;
        }
        let string = '';
        for (const value of args) {
            if (value !== undefined) {
                string += value;
            }
        }
        return string;
    };
    /** Replace carried value */
    replace = (carriedValue, search, replaceWith) => carriedValue?.toString().replace(search, replaceWith);
    /** Trim carried value */
    trim = (carriedValue) => {
        if (carriedValue || carriedValue === 0) {
            return String(carriedValue).trim();
        }
    };
    // region > Array functions
    /** Create array on wrapped computations */
    array = (_, ...values) => values;
    includes = (carriedValue, search, comparison) => {
        const searchSet = new Set(search);
        if (Array.isArray(carriedValue)) {
            for (const value of carriedValue) {
                let compareValue = value;
                if (comparison === 'comparable' && typeof value === 'string') {
                    compareValue = value.toLowerCase();
                }
                if (searchSet.has(compareValue)) {
                    return carriedValue;
                }
            }
        }
        else if (typeof carriedValue === 'string') {
            if ((comparison === 'comparable' && searchSet.has(carriedValue.toLowerCase())) || searchSet.has(carriedValue)) {
                return carriedValue;
            }
        }
        return undefined;
    };
    /** Performant join on the carried value. skips undefined and null values */
    join = (carriedValue, separator, innerProperty) => {
        if (!Array.isArray(carriedValue)) {
            return;
        }
        let value = '';
        for (let i = 0; i < carriedValue.length; i++) {
            const carried = carriedValue[i];
            if (carried === undefined || carried === null) {
                continue;
            }
            if (i > 0) {
                value += separator;
            }
            if (innerProperty) {
                value += carried?.[innerProperty];
            }
            else {
                value += carried;
            }
        }
        return value;
    };
    map = (carriedValue, innerPath) => carriedValue.map((v) => this.getInnerData(v, innerPath));
}
// region Compute
const areInstructions = (value) => {
    if (typeof value !== 'object' || value === null) {
        return false;
    }
    const instruction = value;
    return typeof instruction.function === 'string';
};
const isInstructionsArray = (value) => {
    const isArray = Array.isArray(value);
    if (!isArray) {
        return false;
    }
    const containsInstructions = value.every(areInstructions);
    return isArray && containsInstructions;
};
export const computeInstructions = (instructions, initialValue = null, property, product, productInput, culture, mappings, debug) => {
    let debugEntry = 0;
    const debugLog = (step, phase, details) => {
        if (!debug) {
            return;
        }
        const number = ++debugEntry;
        const colour = number % 2 === 0 ? '\x1b[36m' : '\x1b[35m';
        const reset = '\x1b[0m';
        const label = `#${number} ${step} — ${phase}`;
        console.log(`${colour}┌── ${label} ${'─'.repeat(30)}${reset}`);
        console.dir(details, debug);
        console.log(`${colour}└── END #${number} ${'─'.repeat(30)}${reset}\n`);
    };
    // --- //
    const execute = (instructions, initialValue = null, path = 'root') => {
        let value = initialValue;
        const functions = new ComputeFunctions(product, productInput, culture, property, mappings);
        for (const [index, instruction] of instructions.entries()) {
            const step = `${path}[${index}].${instruction.function}`;
            const func = Reflect.get(functions, instruction.function);
            if (!func) {
                throw new Error(`Unknown compute function ${String(instruction.function)}`);
            }
            const args = [];
            if (instruction.args) {
                for (const [argIndex, arg] of instruction.args.entries()) {
                    const processedArg = isInstructionsArray(arg)
                        ? execute(arg, value, `${step}.args[${argIndex}]`) // resolve nested functions
                        : arg;
                    if (processedArg === undefined) {
                        if (debug) {
                            console.warn(`${step}: argument ${argIndex} resolved to undefined; ` + 'remaining arguments will be skipped');
                        }
                        break;
                    }
                    args.push(processedArg);
                }
            }
            debugLog(step, 'before', { args, input: value });
            value = func(value, ...args);
            debugLog(step, 'after', {
                output: value,
                ...((value === null || value === undefined) && { stopped: true }),
            });
            // break execution on null or undefined
            if (value === null || value === undefined) {
                return;
            }
        }
        return value;
    };
    // --- //
    try {
        const result = execute(instructions, initialValue);
        return Array.isArray(result) ? result : result !== null && result !== undefined ? [result] : undefined;
    }
    catch (err) {
        console.log(err);
    }
};
